'use client';
import React from 'react';
import { Section, Container, GridLayout } from '@layout';
import { Button, Card } from '@ui';
import * as IconSet from '@icons/Icons';
import { withOptionalId } from '@lib/withOptionalId';

import { RadiusScaleDemo, ShadowTokensDemo } from '../design-system/FoundationTokenDemos';

/** Every `IconX` component exported from `@icons/Icons`, alphabetized so the grid
 *  is stable as new icons are added (or removed) from the library. */
type IconComponent = React.FC<React.SVGProps<SVGSVGElement>>;
const ICON_ENTRIES: ReadonlyArray<[string, IconComponent]> = (
  Object.entries(IconSet) as [string, unknown][]
)
  .filter(
    (entry): entry is [string, IconComponent] =>
      entry[0].startsWith('Icon') && typeof entry[1] === 'function'
  )
  .sort(([a], [b]) => a.localeCompare(b));

interface StyleGuideWrapperProps {
  headingText?: string;
  sectionID?: string;
  className?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
}

const INK_TOKENS = [
  '--ink-50',
  '--ink-100',
  '--ink-200',
  '--ink-300',
  '--ink-400',
  '--ink-500',
  '--ink-600',
  '--ink-700',
  '--ink-800',
  '--ink-900',
] as const;

const BRAND_TOKENS = [
  '--brand-50',
  '--brand-100',
  '--brand-200',
  '--brand-300',
  '--brand-400',
  '--brand-500',
  '--brand-600',
  '--brand-700',
  '--brand-800',
  '--brand-900',
] as const;

const SECONDARY_TOKENS = [
  '--secondary-50',
  '--secondary-100',
  '--secondary-200',
  '--secondary-300',
  '--secondary-400',
  '--secondary-500',
  '--secondary-600',
  '--secondary-700',
  '--secondary-800',
  '--secondary-900',
] as const;

const TERTIARY_TOKENS = [
  '--tertiary-50',
  '--tertiary-100',
  '--tertiary-200',
  '--tertiary-300',
  '--tertiary-400',
  '--tertiary-500',
  '--tertiary-600',
  '--tertiary-700',
  '--tertiary-800',
  '--tertiary-900',
] as const;

const SEMANTIC_TOKENS = [
  '--bg',
  '--text',
  '--muted',
  '--primary',
  '--on-primary',
  '--secondary',
  '--on-secondary',
  '--tertiary',
  '--on-tertiary',
  '--accent',
  '--accent-2',
  '--warning',
  '--danger',
  '--success',
  '--info',
  '--border',
] as const;

const FS_SCALE = [
  '--fs-xs',
  '--fs-sm',
  '--fs-md',
  '--fs-lg',
  '--fs-xl',
  '--fs-2xl',
  '--fs-3xl',
] as const;

function SwatchGrid({ tokens }: { tokens: readonly string[] }) {
  return (
    <div className='swatch-grid'>
      {tokens.map((token) => (
        <div key={token} className='swatch'>
          <div className='swatch-chip' style={{ background: `var(${token})` }} />
          <div className='swatch-meta'>{token}</div>
        </div>
      ))}
    </div>
  );
}

const StyleGuideWrapper = ({
  headingText = 'Style guide',
  sectionID,
  className,
  classes,
  containerId,
  containerClasses,
}: StyleGuideWrapperProps) => {
  return (
    <Section {...withOptionalId(sectionID)} className={className || classes}>
      <Container containerId={containerId} containerClasses={containerClasses} classes={classes}>
        <div className='style-guide'>
          <header>
            <h1>{headingText}</h1>
            <p className='style-guide__intro'>
              Reference for typography, color tokens, and common UI patterns. Structural tokens live
              in <code>src/styles/partials/_variables.scss</code>; light theme colors in{' '}
              <code>_colors-light.scss</code>, dark overrides in <code>_colors-dark.scss</code>{' '}
              (aggregated by <code>_colors.scss</code>).
            </p>
          </header>

          <nav aria-label='Style guide sections'>
            <ul className='style-guide__nav'>
              <li>
                <a href='#sg-typography'>Typography</a>
              </li>
              <li>
                <a href='#sg-colors'>Colors</a>
              </li>
              <li>
                <a href='#sg-buttons'>Buttons</a>
              </li>
              <li>
                <a href='#sg-surfaces'>Surfaces &amp; shape</a>
              </li>
              <li>
                <a href='#sg-forms'>Forms</a>
              </li>
              <li>
                <a href='#sg-motion'>Motion</a>
              </li>
              <li>
                <a href='#sg-grid-layout'>Grid layout</a>
              </li>
              <li>
                <a href='#sg-icons'>Icons</a>
              </li>
            </ul>
          </nav>

          <section id='sg-typography' className='style-guide__section'>
            <h2>Typography</h2>
            <p className='sub'>
              Body copy uses <strong>Manrope</strong> via Next.js font variables; monospace stacks
              are defined for code.
            </p>

            <p className='eyebrow'>Eyebrow / overline</p>

            <div>
              <h1>Heading 1</h1>
              <h2>Heading 2</h2>
              <h3>Heading 3</h3>
              <h4>Heading 4</h4>
              <h5>Heading 5</h5>
              <h6>Heading 6</h6>
            </div>

            <p>
              Paragraph with an inline <a href='#sg-typography'>text link</a> using default anchor
              styles. Muted supporting line below.
            </p>
            <p className='small' style={{ color: 'var(--muted)' }}>
              Small / muted paragraph using <code className='small'>--muted</code>.
            </p>

            <h3 className='subsection-title'>Type scale (fluid sizes)</h3>
            <div className='style-guide__type-scale'>
              {FS_SCALE.map((token) => (
                <div key={token} className='style-guide__type-row'>
                  <span className='style-guide__token'>{token}</span>
                  <span style={{ fontSize: `var(${token})`, lineHeight: 1.3 }}>
                    The quick brown fox jumps over the lazy dog.
                  </span>
                </div>
              ))}
            </div>

            <h3 className='subsection-title'>Font stacks</h3>
            <div className='style-guide__mono-sample'>
              <div>
                <span className='style-guide__token'>--font-sans</span>
                <p style={{ fontFamily: 'var(--font-sans)', margin: '0.8rem 0 0' }}>
                  Sans sample — Manrope, system UI fallbacks.
                </p>
              </div>
              <div style={{ marginTop: 'var(--space-3)' }}>
                <span className='style-guide__token'>--font-mono</span>
                <p style={{ fontFamily: 'var(--font-mono)', margin: '0.8rem 0 0' }}>
                  Mono sample — const theme = &apos;oklch&apos;;
                </p>
              </div>
            </div>

            <h3 className='subsection-title'>Lists</h3>
            <ul className='style-guide__list-demo'>
              <li>Unordered list item one</li>
              <li>Unordered list item two</li>
            </ul>
            <ol className='style-guide__list-demo'>
              <li>Ordered list item one</li>
              <li>Ordered list item two</li>
            </ol>
          </section>

          <section id='sg-colors' className='style-guide__section'>
            <h2>Colors</h2>
            <p className='sub'>
              Neutrals, brand, secondary, and tertiary ramps, plus semantic tokens (respect light /
              dark theme).
            </p>

            <h3 className='subsection-title'>Ink (neutral ramp)</h3>
            <SwatchGrid tokens={INK_TOKENS} />

            <h3 className='subsection-title'>Brand</h3>
            <SwatchGrid tokens={BRAND_TOKENS} />

            <h3 className='subsection-title'>Secondary</h3>
            <p className='small' style={{ color: 'var(--muted)', marginTop: 0 }}>
              <code>--secondary</code> maps to <code>--secondary-600</code>.
            </p>
            <SwatchGrid tokens={SECONDARY_TOKENS} />

            <h3 className='subsection-title'>Tertiary</h3>
            <p className='small' style={{ color: 'var(--muted)', marginTop: 0 }}>
              <code>--tertiary</code> maps to <code>--tertiary-600</code>.
            </p>
            <SwatchGrid tokens={TERTIARY_TOKENS} />

            <h3 className='subsection-title'>Semantic</h3>
            <SwatchGrid tokens={SEMANTIC_TOKENS} />

            <h3 className='subsection-title'>Gradients</h3>
            <div className='swatch-grid'>
              {(['--grad-brand', '--grad-hero', '--grad-brand-transparent'] as const).map(
                (token) => (
                  <div key={token} className='swatch'>
                    <div className='swatch-chip' style={{ background: `var(${token})` }} />
                    <div className='swatch-meta'>{token}</div>
                  </div>
                )
              )}
            </div>
          </section>

          <section id='sg-buttons' className='style-guide__section'>
            <h2>Buttons</h2>
            <p className='sub'>
              Shared styles live in <code>src/styles/ui/_actions.scss</code>. Use plain{' '}
              <code>btn</code> / <code>btn-*</code> classes on <code>&lt;button&gt;</code>, or the{' '}
              <code>@ui</code> <code>Button</code> with <code>variant</code> and <code>size</code> (
              <code>className</code> still merges).
            </p>

            <h3 className='subsection-title'>
              Plain CSS &amp; React <code>Button</code>
            </h3>
            <p className='small' style={{ color: 'var(--muted)', marginTop: 0 }}>
              Each group below shows one live row (React). Pair the lines: first is the plain{' '}
              <code>&lt;button&gt;</code> pattern; second is the <code>@ui</code>{' '}
              <code>Button</code> API (<code>variant</code>, <code>size</code>, <code>icon</code> /{' '}
              <code>iconPosition</code> — see <code>src/components/ui/buttons/Button.tsx</code> and{' '}
              <code>src/components/docs/COMPONENTS-UI.md</code>).
            </p>

            <p className='eyebrow' style={{ marginBottom: '0.4rem' }}>
              Semantic fills
            </p>
            <p className='small' style={{ color: 'var(--muted)', margin: 0 }}>
              CSS: <code>className=&quot;btn btn-primary&quot;</code> (plus{' '}
              <code>btn-secondary</code>, <code>btn-tertiary</code>, <code>btn-ghost</code>,{' '}
              <code>btn-warning</code>, <code>btn-danger</code>) on{' '}
              <code>&lt;button type=&quot;button&quot;&gt;</code>.
            </p>
            <p className='small' style={{ color: 'var(--muted)', margin: '0.4rem 0 0.8rem' }}>
              React:{' '}
              <code>&lt;Button variant=&quot;primary&quot; label=&quot;Primary&quot; /&gt;</code> —{' '}
              <code>variant</code> appends <code>btn-{'{variant}'}</code> after <code>btn</code>.
            </p>
            <div className='style-guide__btn-row'>
              <Button type='button' variant='primary' label='Primary' />
              <Button type='button' variant='secondary' label='Secondary' />
              <Button type='button' variant='tertiary' label='Tertiary' />
              <Button type='button' variant='ghost' label='Ghost' />
              <Button type='button' variant='warning' label='Warning' />
              <Button type='button' variant='danger' label='Danger' />
            </div>

            <p className='eyebrow' style={{ margin: '1.6rem 0 0.4rem' }}>
              Icon + label
            </p>
            <p className='small' style={{ color: 'var(--muted)', margin: 0 }}>
              CSS: put icon markup (e.g.{' '}
              <code>&lt;span className=&quot;iconify&quot;&gt;…&lt;/span&gt;</code> or an SVG)
              before or after the label inside the <code>button</code>, consistent with flex rules
              in <code>_actions.scss</code>.
            </p>
            <p className='small' style={{ color: 'var(--muted)', margin: '0.4rem 0 0.8rem' }}>
              React: <code>icon</code> (<code>ReactNode</code>) with{' '}
              <code>iconPosition=&quot;left&quot;</code> (default) or <code>&quot;right&quot;</code>
              ; <code>label</code> is the text beside the icon.
            </p>
            <div className='style-guide__btn-row'>
              <Button type='button' variant='primary' label='Save' icon='✓' iconPosition='left' />
              <Button
                type='button'
                variant='secondary'
                label='Continue'
                icon='→'
                iconPosition='right'
              />
            </div>

            <p className='eyebrow' style={{ margin: '1.6rem 0 0.4rem' }}>
              Outline &amp; link
            </p>
            <p className='small' style={{ color: 'var(--muted)', margin: 0 }}>
              CSS: <code>btn-outline</code>, <code>btn-outline-primary</code>, …{' '}
              <code>btn-outline-danger</code>, and <code>btn-link</code> combined with{' '}
              <code>btn</code>.
            </p>
            <p className='small' style={{ color: 'var(--muted)', margin: '0.4rem 0 0.8rem' }}>
              React: <code>variant=&quot;outline-primary&quot;</code>,{' '}
              <code>variant=&quot;link&quot;</code>, etc. — same classes; <code>variant</code> is
              for display intent, not physical size.
            </p>
            <div className='style-guide__btn-row'>
              <Button type='button' variant='outline' label='Outline' />
              <Button type='button' variant='outline-primary' label='Outline primary' />
              <Button type='button' variant='outline-secondary' label='Outline secondary' />
              <Button type='button' variant='outline-tertiary' label='Outline tertiary' />
              <Button type='button' variant='outline-warning' label='Outline warning' />
              <Button type='button' variant='outline-danger' label='Outline danger' />
              <Button type='button' variant='link' label='Text link button' />
            </div>

            <p className='eyebrow' style={{ margin: '1.6rem 0 0.4rem' }}>
              Sizes (primary)
            </p>
            <p className='small' style={{ color: 'var(--muted)', margin: 0 }}>
              CSS: stack size classes on the fill, e.g.{' '}
              <code>className=&quot;btn btn-primary btn-sm&quot;</code> (<code>btn-xs</code> …{' '}
              <code>btn-xlg</code>).
            </p>
            <p className='small' style={{ color: 'var(--muted)', margin: '0.4rem 0 0.8rem' }}>
              React: <code>variant=&quot;primary&quot; size=&quot;sm&quot;</code> —{' '}
              <code>size</code> maps to <code>btn-sm</code> (and <code>xs</code> / <code>md</code> /{' '}
              <code>lg</code> / <code>xlg</code>).
            </p>
            <div className='style-guide__btn-row'>
              <Button type='button' variant='primary' size='xs' label='Primary xs' />
              <Button type='button' variant='primary' size='sm' label='Primary sm' />
              <Button type='button' variant='primary' size='md' label='Primary md' />
              <Button type='button' variant='primary' size='lg' label='Primary lg' />
              <Button type='button' variant='primary' size='xlg' label='Primary xlg' />
            </div>
          </section>

          <section id='sg-surfaces' className='style-guide__section'>
            <h2>Surfaces &amp; shape</h2>
            <p className='sub'>Elevation backgrounds and radius tokens.</p>

            <h3 className='subsection-title'>Surfaces</h3>
            <div className='style-guide__shadow-row'>
              <div className='style-guide__shadow-card' style={{ background: 'var(--surface-0)' }}>
                <span className='style-guide__token'>--surface-0</span>
                <p style={{ margin: '0.8rem 0 0', color: 'var(--text)' }}>Base / page-level</p>
              </div>
              {(['surface-1', 'surface-2', 'surface-3'] as const).map((cls) => (
                <div key={cls} className={`style-guide__shadow-card ${cls}`}>
                  <span className='style-guide__token'>.{cls}</span>
                  <p style={{ margin: '0.8rem 0 0', color: 'var(--text)' }}>Elevated panel</p>
                </div>
              ))}
            </div>

            <h3 className='subsection-title'>Border radius</h3>
            <RadiusScaleDemo />

            <h3 className='subsection-title'>Shadows</h3>
            <ShadowTokensDemo />
          </section>

          <section id='sg-forms' className='style-guide__section'>
            <h2>Forms</h2>
            <p className='sub'>Default control styles from global form partials.</p>
            <div className='form-grid' style={{ gridTemplateColumns: '1fr', maxWidth: '48rem' }}>
              <div>
                <label htmlFor='sg-sample-input'>Text input</label>
                <input
                  id='sg-sample-input'
                  type='text'
                  placeholder='Placeholder text'
                  autoComplete='off'
                />
              </div>
              <div>
                <label htmlFor='sg-sample-textarea'>Textarea</label>
                <textarea id='sg-sample-textarea' rows={3} placeholder='Longer content…' />
              </div>
            </div>
            <p className='small' style={{ color: 'var(--muted)' }}>
              Focus visible uses <code>--ring-color</code> and <code>--ring-width</code>.
            </p>
          </section>

          <section id='sg-motion' className='style-guide__section'>
            <h2>Motion</h2>
            <ul className='style-guide__list-demo'>
              <li>
                <span className='style-guide__token'>--dur-fast</span> — 120ms
              </li>
              <li>
                <span className='style-guide__token'>--dur</span> — 180ms
              </li>
              <li>
                <span className='style-guide__token'>--dur-slow</span> — 260ms
              </li>
              <li>
                <span className='style-guide__token'>--ease-out</span>,{' '}
                <span className='style-guide__token'>--ease-in-out</span>
              </li>
            </ul>
          </section>

          <section id='sg-grid-layout' className='style-guide__section'>
            <h2>GridLayout</h2>
            <p className='sub'>
              Applies responsive column classes from <code>src/styles/layout/_grids.scss</code> (
              <code>grid-2</code> … <code>grid-6</code>, <code>grid-with-aside</code>). Pass{' '}
              <code>gridColumns</code> for the suffix (e.g. <code>&quot;4&quot;</code> →{' '}
              <code>grid-4</code>).
            </p>

            <h3 className='subsection-title'>Example: three columns</h3>
            <div
              className='style-guide__mono-sample'
              style={{ marginTop: 0, marginBottom: 'var(--space-2)' }}
            >
              <code
                style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >{`<GridLayout gridColumns="3" gridId="sg-grid-3-demo">
  <div>…</div>
  <div>…</div>
  <div>…</div>
</GridLayout>`}</code>
            </div>
            <GridLayout gridColumns='3' gridId='sg-grid-3-demo'>
              {['One', 'Two', 'Three'].map((label) => (
                <div key={label} className='style-guide__shadow-card'>
                  <span className='style-guide__token'>Child</span>
                  <p style={{ margin: '0.5rem 0 0', color: 'var(--text)' }}>{label}</p>
                </div>
              ))}
            </GridLayout>

            <h3 className='subsection-title'>Example: two columns + extra class</h3>
            <p className='small' style={{ color: 'var(--muted)', margin: 0 }}>
              <code>gridClassName</code> merges with <code>grid-{'{columns}'}</code>. On tablet,{' '}
              <code>grid-2 grid-2-c2-right</code> right-aligns the second column when stacked.
            </p>
            <div
              className='style-guide__mono-sample'
              style={{ marginTop: 0, marginBottom: 'var(--space-2)' }}
            >
              <code
                style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >{`<GridLayout gridColumns="2" gridClassName="grid-2-c2-right">
  <div>…</div>
  <div>…</div>
</GridLayout>`}</code>
            </div>
            <GridLayout gridColumns='2' gridClassName='grid-2-c2-right'>
              <div className='style-guide__shadow-card'>
                <span className='style-guide__token'>Main</span>
                <p style={{ margin: '0.5rem 0 0', color: 'var(--text)' }}>
                  Primary column content.
                </p>
              </div>
              <div className='style-guide__shadow-card'>
                <span className='style-guide__token'>Aside</span>
                <p style={{ margin: '0.5rem 0 0', color: 'var(--text)' }}>Secondary column.</p>
              </div>
            </GridLayout>
          </section>

          <section id='sg-icons' className='style-guide__section'>
            <h2>Icons</h2>
            <p className='sub'>
              Every <code>Icon*</code> component re-exported from{' '}
              <code>src/components/icons/Icons</code>. The component name is the card heading; the
              glyph renders below at 32px, inheriting <code>currentColor</code> from the surrounding
              text. Sorted alphabetically — new icons added to the index appear automatically.
            </p>
            <GridLayout gridColumns='4' gridId='sg-icons-grid' className='style-guide-icons'>
              {ICON_ENTRIES.map(([name, IconComp]) => (
                <Card
                  key={name}
                  title={name}
                  as='h2'
                  headerClasses='h5'
                  className='style-guide-icon-card'
                >
                  <div className='style-guide-icon-preview'>
                    <IconComp width={32} height={32} aria-hidden='true' />
                  </div>
                </Card>
              ))}
            </GridLayout>
          </section>
        </div>
      </Container>
    </Section>
  );
};

export default StyleGuideWrapper;
